/**
 * Reusable loading-state helpers for dashboard modules.
 * Copy VAN_DASHBOARD_LOADING_CONFIG pattern when adding new dashboard screens.
 */

export const BLOCK_STATUS = {
  PENDING: 'pending',
  LOADING: 'loading',
  LOADED: 'loaded',
};

/** Delay between revealing cards, charts, and tables within a section. */
export const BLOCK_STAGGER_MS = 150;

export const VAN_DASHBOARD_LOADING_CONFIG = {
  summary: ['kpis'],
  challan: ['status', 'trend', 'table'],
  driver: ['kpis', 'utilization', 'progress'],
  stockTransfer: ['funnel', 'table'],
  settlement: ['kpis', 'table'],
};

export const VAN_DASHBOARD_SECTION_ORDER = [
  'summary',
  'challan',
  'driver',
  'stockTransfer',
  'settlement',
];

export function createInitialLoadingState(config = VAN_DASHBOARD_LOADING_CONFIG) {
  return Object.fromEntries(
    Object.entries(config).map(([section, blocks]) => [
      section,
      Object.fromEntries(blocks.map((block) => [block, BLOCK_STATUS.PENDING])),
    ]),
  );
}

export function updateBlockStatus(state, section, block, status) {
  return {
    ...state,
    [section]: {
      ...state[section],
      [block]: status,
    },
  };
}

export function setSectionLoaded(state, section, config = VAN_DASHBOARD_LOADING_CONFIG) {
  const blocks = config[section] || [];
  return {
    ...state,
    [section]: Object.fromEntries(
      blocks.map((block) => [block, BLOCK_STATUS.LOADED]),
    ),
  };
}

export function isBlockPending(state, section, block) {
  const status = state?.[section]?.[block];
  return status === BLOCK_STATUS.PENDING || status === undefined;
}

export function isBlockLoading(state, section, block) {
  return state?.[section]?.[block] === BLOCK_STATUS.LOADING;
}

export function isBlockLoaded(state, section, block) {
  return state?.[section]?.[block] === BLOCK_STATUS.LOADED;
}

export function createSettledLoadingState(config = VAN_DASHBOARD_LOADING_CONFIG) {
  return Object.fromEntries(
    Object.entries(config).map(([section, blocks]) => [
      section,
      Object.fromEntries(blocks.map((block) => [block, BLOCK_STATUS.LOADED])),
    ]),
  );
}

export function isSectionLoading(state, section, config = VAN_DASHBOARD_LOADING_CONFIG) {
  const blocks = config[section] || [];
  return blocks.some((block) => isBlockLoading(state, section, block));
}

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Reveals dashboard blocks one after another once section data is available.
 */
export async function revealSectionBlocks({
  section,
  blocks,
  setStatus,
  staggerMs = BLOCK_STAGGER_MS,
  isCancelled = () => false,
}) {
  setStatus(section, blocks[0], BLOCK_STATUS.LOADED);

  for (let index = 1; index < blocks.length; index += 1) {
    const block = blocks[index];
    setStatus(section, block, BLOCK_STATUS.LOADING);
    await sleep(staggerMs);
    if (isCancelled()) {
      return;
    }
    setStatus(section, block, BLOCK_STATUS.LOADED);
  }
}
